import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Select,
  Checkbox,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  createWorkSecurityRole,
  fetchWorkSecurityGroups,
  WorkSecurityGroup,
} from "services/work-security.service";
import { WorkSecurityArea } from "services/work-security-areas.service";
import { setIsModalxOpen, setDestroy } from "components/modalx/modalx.slice";
import AreaFormField, {
  defaultAreaSelection,
} from "features/work-security-areas/AreaFormField";
import { resolveAreaErrorMessage } from "features/work-security-areas/area-errors";
import WorkSecurityRulesEditor, {
  buildOrderedSecurityRules,
} from "./WorkSecurityRulesEditor";

interface WorkSecurityRoleFormModalProps {
  /** Accessible spheres (PR-2 §P4) — a Select is only offered in "all spheres" mode. */
  areas?: WorkSecurityArea[];
  /** The module header's concretely-selected sphere; null/undefined = "all spheres" mode. */
  selectedArea?: WorkSecurityArea | null;
  onSuccess?: () => void;
}

export default function WorkSecurityRoleFormModal({
  areas = [],
  selectedArea,
  onSuccess,
}: WorkSecurityRoleFormModalProps) {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [rules, setRules] = useState<string[]>([""]);
  const [groups, setGroups] = useState<WorkSecurityGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  // Only meaningful in "all spheres" mode (2+ spheres, none concretely
  // selected) — see AreaFormField. Otherwise never rendered/sent.
  const [selectedAreaId, setSelectedAreaId] = useState(() => defaultAreaSelection(areas));
  // "Privaloma įdarbinant" — on by default: most instructions really are a
  // hiring blocker, and the roles that aren't are the exception to opt out of.
  const [requiredAtHiring, setRequiredAtHiring] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const showAreaField = areas.length > 1;

  useEffect(() => {
    if (!companyId) return;
    // Groups belong to a single sphere: offering another sphere's group would
    // only earn an AREA_GROUP_MISMATCH from the backend on save.
    fetchWorkSecurityGroups(companyId, selectedAreaId ? { areaId: selectedAreaId } : undefined)
      .then((data) => setGroups(data.groups || []))
      .catch((err) => console.error("Failed to load work security groups", err));
  }, [companyId, selectedAreaId]);

  // Switching the sphere invalidates a group picked from the previous one.
  useEffect(() => {
    if (selectedGroup && !groups.some((item) => item.id === selectedGroup)) {
      setSelectedGroup("");
    }
  }, [groups, selectedGroup]);

  const handleFormSubmit = async () => {
    if (!companyId) return;
    if (!roleName.trim()) {
      setError(
        t("role_name_required", { defaultValue: "Role name is required" })
      );
      return;
    }
    // Belt-and-suspenders: the Select never leaves this empty (no blank
    // option, pre-filled with a sensible default), but a role must never be
    // silently filed without an explicit sphere once there is more than one.
    if (showAreaField && !selectedArea && !selectedAreaId) {
      setError(
        t("area_field_required", { defaultValue: "Please choose an instruction sphere." })
      );
      return;
    }

    const filteredRules = buildOrderedSecurityRules(rules);

    try {
      setSaving(true);
      setError("");
      const newRole = await createWorkSecurityRole(companyId, {
        name: roleName.trim(),
        description: roleDescription.trim(),
        rules: filteredRules,
        group: selectedGroup || null,
        // HARD RULE (PR-2 §P1): with 0-1 accessible spheres, omit `area`
        // entirely so this request stays byte-for-byte what it was before
        // spheres existed — the backend defaults it for us in that case.
        ...(showAreaField
          ? { area: selectedArea ? selectedArea.id : selectedAreaId }
          : {}),
        requiredAtHiring,
      });

      // Close modal
      dispatch(setIsModalxOpen(false));
      dispatch(setDestroy());

      if (onSuccess) {
        onSuccess();
      }

      // Redirect to stepped details container
      navigate(`/app/${companyId}/work-security/${newRole.role.id}`);
    } catch (err: any) {
      console.error("Failed to create role", err);
      setError(
        resolveAreaErrorMessage(err, t) ||
          err?.response?.data?.message ||
          t("failed_create_role", { defaultValue: "Failed to create role." })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormLayout>
        {error && <Banner tone="critical">{error}</Banner>}

        <TextField
          label={t("role_name", { defaultValue: "Role Name" })}
          value={roleName}
          onChange={setRoleName}
          placeholder={t("role_name_placeholder", {
            defaultValue: "e.g., Warehouse Operator",
          })}
          disabled={saving}
          autoComplete="off"
          requiredIndicator
        />

        <TextField
          label={t("role_description", { defaultValue: "Description" })}
          value={roleDescription}
          onChange={setRoleDescription}
          placeholder={t("role_desc_placeholder", {
            defaultValue: "Brief description of the role requirements...",
          })}
          disabled={saving}
          multiline={3}
          autoComplete="off"
        />

        <Select
          label={t("group", { defaultValue: "Group" })}
          options={[
            { label: t("no_group", { defaultValue: "No group" }), value: "" },
            ...groups.map((group) => ({ label: group.name, value: group.id })),
          ]}
          value={selectedGroup}
          onChange={setSelectedGroup}
          disabled={saving}
        />

        <AreaFormField
          areas={areas}
          selectedArea={selectedArea}
          value={selectedAreaId}
          onChange={setSelectedAreaId}
          disabled={saving}
        />

        <Checkbox
          label={t("work_security_required_at_hiring", {
            defaultValue: "Privaloma įdarbinant",
          })}
          helpText={t("work_security_required_at_hiring_hint", {
            defaultValue:
              "Šios instrukcijos nepasirašius, darbuotojo nepavyks įdarbinti.",
          })}
          checked={requiredAtHiring}
          onChange={setRequiredAtHiring}
          disabled={saving}
        />

        <WorkSecurityRulesEditor
          rules={rules}
          onRulesChange={setRules}
          disabled={saving}
        />

        <div className="flex justify-end gap-3 mt-4 border-t border-gray-150 pt-4">
          <Button
            onClick={() => {
              dispatch(setIsModalxOpen(false));
              dispatch(setDestroy());
            }}
            disabled={saving}
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="primary"
            onClick={handleFormSubmit}
            loading={saving}
            disabled={!roleName.trim()}
          >
            {t("save", { defaultValue: "Save" })}
          </Button>
        </div>
      </FormLayout>
    </Form>
  );
}
